/**
 * HeartbeatSync
 *
 * A minimal, elegant toggle that lives in the top-right of the nav bar.
 * Default: OFF. On click it shows a permission dialog before ever touching
 * the microphone. When active, a small red pulse dot confirms the mic is live.
 *
 * The component only renders the UI — all mic logic lives in useBpmPulse.
 * Wraps around HeartbeatContext so the whole app shares one BPM stream.
 */

import { useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { useHeartbeat } from "@/contexts/HeartbeatContext";

export default function HeartbeatSync() {
  const { isListening, isHeartbeat, toggleMic, bpm } = useHeartbeat();
  const [showPrompt, setShowPrompt] = useState(false);

  function handleToggle() {
    if (isListening) {
      toggleMic();
    } else {
      setShowPrompt(true);
    }
  }

  function confirmEnable() {
    setShowPrompt(false);
    toggleMic();
  }

  return (
    <>
      {/* ── Toggle pill ── */}
      <button
        onClick={handleToggle}
        aria-pressed={isListening}
        aria-label={isListening ? "Stop heartbeat sync" : "Enable heartbeat sync"}
        className={`
          group relative inline-flex items-center gap-1.5
          font-sans text-[9px] uppercase tracking-[0.18em]
          border rounded-full px-2.5 py-1 btn-press transition-all duration-300
          ${isListening
            ? "border-primary/40 text-primary bg-primary/8 hover:bg-primary/14"
            : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
          }
        `}
      >
        {/* Mic icon */}
        {isListening ? (
          <Mic className="w-2.5 h-2.5 flex-shrink-0" />
        ) : (
          <MicOff className="w-2.5 h-2.5 flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
        )}

        {/* Label */}
        <span className="hidden sm:inline">
          {isListening ? "Syncing" : "Heartbeat"}
        </span>

        {/* Live BPM badge */}
        {isListening && (
          <span className="tabular-nums text-[8px] opacity-70">
            {bpm}
          </span>
        )}

        {/* Breathing dot when listening */}
        {isListening && (
          <span
            className={`w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 ${
              isHeartbeat ? "animate-pulse" : "opacity-60"
            }`}
          />
        )}
      </button>

      {/* ── Permission dialog ── */}
      {showPrompt && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hb-dialog-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-background/70 backdrop-blur-sm"
            onClick={() => setShowPrompt(false)}
          />

          {/* Panel */}
          <div className="relative z-10 bg-card border border-border rounded-[var(--radius)] shadow-xl max-w-[320px] w-full p-6 flex flex-col gap-5 animate-fade-up">
            {/* Icon */}
            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
              <Mic className="w-3.5 h-3.5 text-primary" />
            </div>

            {/* Copy */}
            <div className="text-center space-y-1.5">
              <p
                id="hb-dialog-title"
                className="font-serif text-sm text-foreground"
              >
                Heartbeat Sync
              </p>
              <p className="font-sans text-[11px] text-muted-foreground leading-relaxed">
                Allow microphone access to detect your heartbeat in real time.
                Your pulse will gently animate the Love Language FM wordmark —
                turning every dedication into something literally felt.
              </p>
            </div>

            {/* Privacy note */}
            <p className="font-sans text-[9px] text-muted-foreground/60 text-center tracking-wide uppercase">
              No data stored · Real-time only · You can stop at any time
            </p>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                onClick={confirmEnable}
                className="w-full font-sans text-[10px] uppercase tracking-[0.2em] bg-primary text-primary-foreground rounded-full py-2.5 btn-press hover:bg-primary/90 transition-colors"
              >
                Allow access
              </button>
              <button
                onClick={() => setShowPrompt(false)}
                className="w-full font-sans text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors py-2"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
